import { themeVariants } from 'style-utils/theme';
import styled, { css } from 'styled-components';
import {
  background,
  BackgroundProps,
  border,
  BorderProps,
  borderRadius,
  BorderRadiusProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from 'styled-system';

export type ButtonProps = LayoutProps &
  SpaceProps &
  FlexboxProps &
  BorderProps &
  BorderRadiusProps &
  PositionProps &
  TypographyProps &
  BackgroundProps & {
    variant?:
      | 'default'
      | 'secondary'
      | 'outline'
      | 'rounded'
      | 'roundedOutline'
      | 'ghost'
      | 'white'
      | 'whiteSecondaryVariant';
  } & { buttonSize?: 'default' };

const defaultCss = css`
  border-width: var(--btn-border-width);
  border-color: var(--btn-primary-border);
  background-color: var(--btn-primary);
  color: var(--btn-primary-text);
  &:disabled {
    border-color: var(--btn-primary-disabled-bg);
    background-color: var(--btn-primary-disabled-border);
  }
`;

const secondaryCss = css`
  border-width: var(--btn-border-width);
  border-color: var(--btn-secondary-border);
  background-color: var(--btn-secondary);
  color: var(--btn-secondary-text);
`;

const outlinedCss = css`
  border-width: 1px;
  border-color: var(--btn-outlined-border);
  background-color: var(--btn-outlined);
  color: var(--btn-outlined-text);
  border-radius: var(--btn-radius) !important;
`;

const ghostCss = css`
  border: none;
  background: transparent;
  color: inherit;
`;

const roundedCss = css`
  ${defaultCss}
  border: none;
  border-radius: 9999px;
`;

const roundedOutlineCss = css`
  border-radius: 9999px;
  ${outlinedCss}
`;
const whiteCss = css`
  color: var(--btn-primary-text);
  background: white;
  border: 1px solid #000000;
`;

const buttonVariants = themeVariants('vendor', 'variant', {
  default: {
    sisal: defaultCss,
    pokerstars: defaultCss,
    snai: defaultCss,
  },
  secondary: {
    sisal: secondaryCss,
    pokerstars: secondaryCss,
    snai: secondaryCss,
  },
  outline: { sisal: outlinedCss, pokerstars: outlinedCss, snai: outlinedCss },
  rounded: { sisal: roundedCss, pokerstars: roundedCss, snai: roundedCss },
  roundedOutline: {
    sisal: roundedOutlineCss,
    pokerstars: roundedOutlineCss,
    snai: roundedOutlineCss,
  },
  ghost: { sisal: ghostCss, pokerstars: ghostCss, snai: ghostCss },
  white: {
    sisal: whiteCss,
    pokerstars: whiteCss,
    snai: whiteCss,
  },
  whiteSecondaryVariant: {
    sisal: css`
      background: white;
      border: 1px solid #bed62f;
    `,
    pokerstars: whiteCss,
    snai: whiteCss,
  },
});

const defaultSisalSize = css`
  height: 44px;
  padding: 6px 16px;
`;

const defaultPsSize = css`
  height: 36px;
  padding: 6px 16px;
`;

const defaultSnaiSize = css`
  height: 36px;
  padding: 8px 12px;
`;

const buttonSizes = themeVariants('vendor', 'buttonSize', {
  default: {
    sisal: defaultSisalSize,
    pokerstars: defaultPsSize,
    snai: defaultSnaiSize,
  },
});

const Button = styled.button<ButtonProps>`
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.025em;
  font-weight: bold;

  border-style: solid;

  cursor: pointer;

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  ${buttonVariants}
  ${buttonSizes}

  ${layout}
  ${space}
  ${flexbox}
  ${border}
  ${borderRadius}
  ${position}
  ${typography}
  ${background}
`;

Button.defaultProps = {
  variant: 'default',
  buttonSize: 'default',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--btn-radius)',
  type: 'button',
};

export { Button };
